/**
 * Platform detection utilities
 * Helps identify which platform the app is running on
 * Useful for platform-specific code paths
 */

/**
 * Check if running in Electron
 */
export function isElectron(): boolean {
  return typeof window !== 'undefined' && window.electron !== undefined;
}

/**
 * Check if running as browser extension (chrome.runtime present).
 */
export function isExtension(): boolean {
  const w = window as Window & { chrome?: { runtime?: { id?: string } } };
  return (
    typeof w.chrome !== 'undefined' &&
    w.chrome.runtime !== undefined &&
    w.chrome.runtime.id !== undefined
  );
}

/**
 * True when this page is the extension's own UI (popup, options, full app tab).
 * Unlike web + content-script detection, no tabregistered handshake is needed.
 */
export function isExtensionApp(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.protocol === 'chrome-extension:') return true;
  if (window.troveApp?.isExtension?.() === true) return true;
  return isExtension();
}

/**
 * Check if running as web app
 */
export function isWeb(): boolean {
  return !isElectron() && !isExtension();
}

/**
 * Get the current platform
 */
export type Platform = 'electron' | 'extension' | 'web';

export function getPlatform(): Platform {
  if (isElectron()) return 'electron';
  if (isExtension()) return 'extension';
  return 'web';
}

/** Desktop/mobile OS inferred from the browser (for download recommendations). */
export type ClientOS = 'macos' | 'windows' | 'linux' | 'ios' | 'android' | 'unknown';

export function getClientOS(): ClientOS {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  const platform = (navigator.platform ?? '').toLowerCase();

  if (/iPhone|iPad|iPod/.test(ua) || (platform.includes('mac') && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/i.test(ua)) return 'android';
  if (platform.includes('mac') || /Mac OS X/i.test(ua)) return 'macos';
  if (platform.includes('win') || /Windows/i.test(ua)) return 'windows';
  if (platform.includes('linux') || /Linux/i.test(ua)) return 'linux';
  return 'unknown';
}

/**
 * Get Electron API (if available)
 * Returns null if not in Electron environment
 */
export function getElectronAPI() {
  if (!isElectron()) return null;
  return window.electron!;
}

